import maplibregl from 'maplibre-gl';

/**
 * LeafletShim — provides a Leaflet-compatible `L` API backed by MapLibre GL JS.
 *
 * This allows UIController and other code that uses L.polyline, L.marker,
 * L.divIcon, L.icon, L.layerGroup to work unchanged while the underlying
 * rendering is MapLibre vector tiles.
 *
 * Only the subset of Leaflet API actually used by Zephyr is implemented.
 * This is NOT a full Leaflet reimplementation.
 */

/**
 * Reference to the active MapLibre map instance.
 * Set by MapService.init() after map creation.
 */
let _map = null;
let _nextId = 1;

export function setMapInstance(map) {
    _map = map;
}

// ─────────────────────────────────────────────────────────────────────
// L.divIcon / L.icon — returns an icon config object
// ─────────────────────────────────────────────────────────────────────

function divIcon(options = {}) {
    return { type: 'div', ...options };
}

function icon(options = {}) {
    return { type: 'image', ...options };
}

// ─────────────────────────────────────────────────────────────────────
// L.marker — creates a MapLibre Marker from icon config
// ─────────────────────────────────────────────────────────────────────

function marker(latlng, options = {}) {
    const lat = Array.isArray(latlng) ? latlng[0] : latlng.lat;
    const lng = Array.isArray(latlng) ? latlng[1] : latlng.lng;

    const iconDef = options.icon || { type: 'div', html: '📍' };

    // Create DOM element
    const el = document.createElement('div');
    el.style.cursor = 'pointer';

    if (iconDef.type === 'image' && iconDef.iconUrl) {
        const size = iconDef.iconSize ? iconDef.iconSize[0] : 32;
        el.innerHTML = `<img src="${iconDef.iconUrl}" style="width:${size}px;height:${size}px;" />`;
    } else if (iconDef.type === 'div' && iconDef.html) {
        el.innerHTML = iconDef.html;
        if (iconDef.className) el.className = iconDef.className;
    } else {
        el.innerHTML = '<span style="font-size:1.5rem;">📍</span>';
    }

    const mlMarker = new maplibregl.Marker({ element: el })
        .setLngLat([lng, lat]);

    let popup = null;
    let added = false;

    const wrapper = {
        _mlMarker: mlMarker,
        _el: el,
        _latlng: [lat, lng],
        _popup: null,

        addTo(mapOrGroup) {
            if (mapOrGroup && mapOrGroup._mlMap) {
                mlMarker.addTo(mapOrGroup._mlMap);
            } else if (_map) {
                mlMarker.addTo(_map);
            }
            added = true;
            return wrapper;
        },

        bindPopup(content, popupOptions = {}) {
            popup = new maplibregl.Popup({
                offset: iconDef.popupAnchor ? iconDef.popupAnchor[1] * -1 : 25,
                maxWidth: popupOptions.maxWidth || '300px'
            }).setHTML(content);
            mlMarker.setPopup(popup);
            wrapper._popup = popup;
            return wrapper;
        },

        setPopupContent(content) {
            if (popup) popup.setHTML(content);
            else wrapper.bindPopup(content);
            return wrapper;
        },

        getPopup() {
            return popup;
        },

        remove() {
            mlMarker.remove();
            added = false;
            return wrapper;
        },

        setLatLng(ll) {
            const la = Array.isArray(ll) ? ll[0] : ll.lat;
            const ln = Array.isArray(ll) ? ll[1] : ll.lng;
            mlMarker.setLngLat([ln, la]);
            return wrapper;
        },

        on(event, handler) {
            el.addEventListener(event, handler);
            return wrapper;
        }
    };

    return wrapper;
}

// ─────────────────────────────────────────────────────────────────────
// L.polyline — creates a GeoJSON line layer on the MapLibre map
// ─────────────────────────────────────────────────────────────────────

function polyline(latlngs, options = {}) {
    const id = `shim-polyline-${_nextId++}`;
    const sourceId = `${id}-src`;

    // Convert [{lat, lng}] or [[lat, lng]] to [[lng, lat]] for GeoJSON
    const coords = latlngs.map(c => {
        if (Array.isArray(c)) return [c[1], c[0]];
        return [c.lng, c.lat];
    });

    const geojson = {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords }
    };

    let added = false;
    let clickHandler = null;

    const wrapper = {
        _id: id,
        _sourceId: sourceId,
        _coords: coords,

        addTo(mapOrGroup) {
            const targetMap = (mapOrGroup && mapOrGroup._mlMap) ? mapOrGroup._mlMap : _map;
            if (!targetMap) return wrapper;

            // If adding to a layerGroup, register for cleanup
            if (mapOrGroup && mapOrGroup._layers) {
                mapOrGroup._layers.push(wrapper);
            }

            if (targetMap.getSource(sourceId)) {
                targetMap.getSource(sourceId).setData(geojson);
            } else {
                targetMap.addSource(sourceId, { type: 'geojson', data: geojson });
                targetMap.addLayer({
                    id: id,
                    type: 'line',
                    source: sourceId,
                    paint: {
                        'line-color': options.color || '#667eea',
                        'line-width': options.weight || 5,
                        'line-opacity': options.opacity !== undefined ? options.opacity : 0.7
                    }
                });
            }

            added = true;

            // Wire click handler if set
            if (clickHandler) {
                targetMap.on('click', id, clickHandler);
                targetMap.on('mouseenter', id, () => { targetMap.getCanvas().style.cursor = 'pointer'; });
                targetMap.on('mouseleave', id, () => { targetMap.getCanvas().style.cursor = ''; });
            }

            return wrapper;
        },

        remove() {
            if (_map && _map.getLayer(id)) _map.removeLayer(id);
            if (_map && _map.getSource(sourceId)) _map.removeSource(sourceId);
            added = false;
            return wrapper;
        },

        on(event, handler) {
            if (event === 'click') {
                clickHandler = handler;
                // If already added, wire immediately
                if (added && _map) {
                    _map.on('click', id, handler);
                    _map.on('mouseenter', id, () => { _map.getCanvas().style.cursor = 'pointer'; });
                    _map.on('mouseleave', id, () => { _map.getCanvas().style.cursor = ''; });
                }
            }
            return wrapper;
        },

        getBounds() {
            const bounds = new maplibregl.LngLatBounds();
            coords.forEach(c => bounds.extend(c));
            return bounds;
        },

        bindPopup(content) {
            // Polyline popups — create on click
            if (_map && added) {
                _map.on('click', id, (e) => {
                    new maplibregl.Popup().setLngLat(e.lngLat).setHTML(content).addTo(_map);
                });
            }
            return wrapper;
        }
    };

    return wrapper;
}

// ─────────────────────────────────────────────────────────────────────
// L.layerGroup — a container that holds markers/polylines for bulk removal
// ─────────────────────────────────────────────────────────────────────

function layerGroup() {
    const layers = [];

    const group = {
        _layers: layers,
        _mlMap: _map,

        addTo(mapLike) {
            // Store reference to map for items added later
            if (mapLike && mapLike._mlMap) group._mlMap = mapLike._mlMap;
            else if (mapLike === _map) group._mlMap = _map;
            // If mapLike is the MapLibre map instance directly
            else if (mapLike && mapLike.addLayer) group._mlMap = mapLike;
            return group;
        },

        addLayer(item) {
            layers.push(item);
            // Auto-add to map if the group is already on a map
            if (group._mlMap && item && typeof item.addTo === 'function') {
                item.addTo(group);
            }
            return group;
        },

        clearLayers() {
            layers.forEach(item => {
                if (item && typeof item.remove === 'function') item.remove();
            });
            layers.length = 0;
            return group;
        },

        removeLayer(item) {
            const idx = layers.indexOf(item);
            if (idx > -1) {
                layers.splice(idx, 1);
                if (item && typeof item.remove === 'function') item.remove();
            }
            return group;
        }
    };

    return group;
}

// ─────────────────────────────────────────────────────────────────────
// L.map — NOT implemented here. MapService.init() handles map creation.
// This is a stub so `import L from` doesn't break if someone checks L.map.
// ─────────────────────────────────────────────────────────────────────

function map() {
    throw new Error('Use MapService.init() instead of L.map()');
}

// ─────────────────────────────────────────────────────────────────────
// Export as the `L` namespace
// ─────────────────────────────────────────────────────────────────────

const L = {
    map,
    marker,
    polyline,
    divIcon,
    icon,
    layerGroup,
    DomUtil: { create: (tag) => document.createElement(tag) }
};

export default L;
